/**
 * iter-261 — Stems Marketplace browse page (`/marketplace`).
 *
 * Public browse experience — anyone can hit `/marketplace` and search /
 * filter / download. Like + delete actions require a signed-in session
 * (the cards open the auth chip when an anon user clicks Like).
 *
 * Filter chips reflect the iter-253 auto-tagged manifest fields:
 *   • genre dropdown
 *   • key root + quality
 *   • tempo range (min/max)
 *   • sort: newest / most liked / most downloaded
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import ElpmLogo from "@/components/ElpmLogo";
import { toast } from "sonner";
import {
  Search,
  Music,
  ArrowLeft,
  Loader2,
} from "lucide-react";
import {
  browseStemPacks,
  ALLOWED_GENRES,
  SORT_OPTIONS,
  KEY_ROOTS,
} from "@/lib/marketplaceApi";
import previewPlayer from "@/lib/marketplacePreviewPlayer";
import useAuth from "@/lib/useAuth";
import StemPackCard from "@/components/marketplace/StemPackCard";

export default function MarketplacePage() {
  const navigate = useNavigate();
  const { user, signIn } = useAuth();
  const signedIn = !!user;

  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filter state.
  const [q, setQ] = useState("");
  const [genre, setGenre] = useState("");
  const [keyRoot, setKeyRoot] = useState("");
  const [keyQuality, setKeyQuality] = useState("");
  const [tempoMin, setTempoMin] = useState("");
  const [tempoMax, setTempoMax] = useState("");
  const [sort, setSort] = useState("recent");

  // Debounce filter changes (300 ms) so each keystroke doesn't fire a
  // fetch.
  const debouncedFilters = useDebounced(
    { q, genre, keyRoot, keyQuality, tempoMin, tempoMax, sort },
    300,
  );

  const fetchPacks = useCallback(async (signal) => {
    setLoading(true);
    try {
      const data = await browseStemPacks({
        q: debouncedFilters.q,
        genre: debouncedFilters.genre,
        keyRoot: debouncedFilters.keyRoot,
        keyQuality: debouncedFilters.keyQuality,
        tempoMin: debouncedFilters.tempoMin ? Number(debouncedFilters.tempoMin) : undefined,
        tempoMax: debouncedFilters.tempoMax ? Number(debouncedFilters.tempoMax) : undefined,
        sort: debouncedFilters.sort,
        limit: 60,
      });
      if (!signal?.aborted) setPacks(data);
    } catch (e) {
      if (!signal?.aborted) toast.error("Could not load marketplace", { description: e.message });
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [debouncedFilters]);

  useEffect(() => {
    const ctrl = new AbortController();
    fetchPacks(ctrl.signal);
    return () => ctrl.abort();
  }, [fetchPacks]);

  // iter-263 — Stop any active stem preview when leaving the marketplace
  // route (e.g. user clicks Back or Remix on a card and navigates away).
  // Without this the HTMLAudioElement would keep playing while the user
  // sits in /studio.
  useEffect(() => {
    return () => previewPlayer.stop();
  }, []);

  // Optimistic mutation handlers from the cards.
  const handlePackMutated = useCallback((updated) => {
    setPacks((cur) => cur.map((p) => (p.pack_id === updated.pack_id ? { ...p, ...updated } : p)));
  }, []);
  const handlePackDeleted = useCallback((pack_id) => {
    setPacks((cur) => cur.filter((p) => p.pack_id !== pack_id));
  }, []);

  const resultCount = packs.length;
  const heroSubtitle = useMemo(() => {
    if (loading) return "Loading packs…";
    if (resultCount === 0) return "No packs match your filters — try widening the range.";
    return `${resultCount} pack${resultCount === 1 ? "" : "s"} available · all auto-tagged with tempo + key`;
  }, [loading, resultCount]);

  return (
    <div
      data-testid="marketplace-page"
      className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-950 to-violet-950 text-white"
    >
      {/* Header */}
      <header className="sticky top-0 z-30 border-b border-white/5 bg-slate-950/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-6 py-4">
          <div className="flex items-center gap-3">
            <button
              type="button"
              data-testid="marketplace-back-btn"
              onClick={() => navigate("/studio")}
              className="rounded-lg p-1.5 text-slate-400 transition hover:bg-white/5 hover:text-white"
              title="Back to studio"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div className="flex items-center gap-2">
              <ElpmLogo className="h-8" testId="marketplace-logo" />
              <span className="font-mono text-xs uppercase tracking-[0.18em] text-slate-300">
                Stems Marketplace
              </span>
            </div>
          </div>
          {!signedIn && (
            <button
              type="button"
              data-testid="marketplace-signin-btn"
              onClick={signIn}
              className="rounded-xl bg-gradient-to-r from-amber-400 to-orange-500 px-4 py-2 text-xs font-semibold text-slate-950 transition hover:brightness-110"
            >
              Sign in to publish
            </button>
          )}
        </div>
      </header>

      {/* Hero */}
      <section className="mx-auto max-w-7xl px-6 py-10">
        <h1 className="text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl">
          <span className="bg-gradient-to-r from-emerald-300 via-cyan-300 to-violet-300 bg-clip-text text-transparent">
            Producer-grade stems,
          </span>
          <br />
          ready to drop into your DAW.
        </h1>
        <p className="mt-3 max-w-2xl text-base text-slate-400">{heroSubtitle}</p>
      </section>

      {/* Filters */}
      <section className="mx-auto max-w-7xl px-6 pb-6">
        <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-white/5 bg-slate-900/40 p-3 backdrop-blur-sm">
          {/* Search */}
          <div className="relative flex min-w-[220px] flex-1 items-center">
            <Search className="absolute left-3 h-4 w-4 text-slate-500" />
            <input
              data-testid="marketplace-search-input"
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search title, tags…"
              className="w-full rounded-xl border border-white/10 bg-slate-950/40 py-2 pl-9 pr-3 text-sm text-white placeholder-slate-600 focus:border-violet-400/60 focus:outline-none focus:ring-1 focus:ring-violet-400/40"
            />
          </div>
          {/* Genre */}
          <select
            data-testid="marketplace-genre-filter"
            value={genre}
            onChange={(e) => setGenre(e.target.value)}
            className="rounded-xl border border-white/10 bg-slate-950/40 px-3 py-2 text-sm text-white focus:border-violet-400/60 focus:outline-none"
          >
            <option value="">All genres</option>
            {ALLOWED_GENRES.map((g) => (
              <option key={g} value={g}>{g}</option>
            ))}
          </select>
          {/* Key root */}
          <select
            data-testid="marketplace-key-root-filter"
            value={keyRoot}
            onChange={(e) => setKeyRoot(e.target.value)}
            className="rounded-xl border border-white/10 bg-slate-950/40 px-3 py-2 text-sm text-white focus:border-violet-400/60 focus:outline-none"
          >
            <option value="">Any root</option>
            {KEY_ROOTS.map((k) => (
              <option key={k} value={k}>{k}</option>
            ))}
          </select>
          <select
            data-testid="marketplace-key-quality-filter"
            value={keyQuality}
            onChange={(e) => setKeyQuality(e.target.value)}
            className="rounded-xl border border-white/10 bg-slate-950/40 px-3 py-2 text-sm text-white focus:border-violet-400/60 focus:outline-none"
          >
            <option value="">Any quality</option>
            <option value="major">major</option>
            <option value="minor">minor</option>
          </select>
          {/* Tempo */}
          <div className="flex items-center gap-1">
            <input
              data-testid="marketplace-tempo-min"
              type="number"
              min={20}
              max={300}
              value={tempoMin}
              onChange={(e) => setTempoMin(e.target.value)}
              placeholder="Min BPM"
              className="w-[90px] rounded-xl border border-white/10 bg-slate-950/40 px-3 py-2 text-sm text-white placeholder-slate-600 focus:border-violet-400/60 focus:outline-none"
            />
            <span className="text-slate-600">–</span>
            <input
              data-testid="marketplace-tempo-max"
              type="number"
              min={20}
              max={300}
              value={tempoMax}
              onChange={(e) => setTempoMax(e.target.value)}
              placeholder="Max BPM"
              className="w-[90px] rounded-xl border border-white/10 bg-slate-950/40 px-3 py-2 text-sm text-white placeholder-slate-600 focus:border-violet-400/60 focus:outline-none"
            />
          </div>
          {/* Sort */}
          <select
            data-testid="marketplace-sort-select"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="rounded-xl border border-white/10 bg-slate-950/40 px-3 py-2 text-sm text-white focus:border-violet-400/60 focus:outline-none"
          >
            {SORT_OPTIONS.map((s) => (
              // iter-263 — single string child (template literal) so the
              // Emergent visual-edits library doesn't wrap a dynamic
              // expression in <span style={display:contents}> here.
              // HTML spec forbids non-text children inside <option>,
              // and that wrapper trips React's hydration warning.
              <option key={s.value} value={s.value}>{`Sort: ${s.label}`}</option>
            ))}
          </select>
        </div>
      </section>

      {/* Grid */}
      <section className="mx-auto max-w-7xl px-6 pb-20">
        {loading && packs.length === 0 ? (
          <div className="flex items-center justify-center gap-2 py-20 text-slate-500" data-testid="marketplace-loading">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span>Loading marketplace…</span>
          </div>
        ) : packs.length === 0 ? (
          <div
            className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-white/10 bg-slate-900/30 py-20 text-center"
            data-testid="marketplace-empty"
          >
            <Music className="h-9 w-9 text-slate-700" />
            <p className="text-base font-medium text-slate-300">No stem packs match your filters</p>
            <p className="max-w-md text-sm text-slate-500">
              Be the first to publish — extract stems in the Studio, then
              hit the green &quot;Publish to marketplace&quot; button below the
              download link.
            </p>
            <button
              type="button"
              data-testid="marketplace-back-to-studio"
              onClick={() => navigate("/studio")}
              className="mt-2 rounded-xl bg-gradient-to-r from-violet-500 to-fuchsia-500 px-5 py-2.5 text-sm font-semibold text-white transition hover:brightness-110"
            >
              Open Studio
            </button>
          </div>
        ) : (
          <div
            data-testid="marketplace-grid"
            className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3"
          >
            {packs.map((pack) => (
              <StemPackCard
                key={pack.pack_id}
                pack={pack}
                signedIn={signedIn}
                onMutated={handlePackMutated}
                onDeleted={handlePackDeleted}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────

function useDebounced(value, ms) {
  const [v, setV] = useState(value);
  const valueRef = useRef(value);
  valueRef.current = value;
  const key = JSON.stringify(value);
  useEffect(() => {
    const id = setTimeout(() => setV(valueRef.current), ms);
    return () => clearTimeout(id);
  }, [key, ms]);
  return v;
}
